// Pricing migration 17 (ADO-2248): the shared price lookup (ADO-2248 DEC-2, DEC-4).
//
// One Zustand store (ADR-004) every product surface reads its price from, keyed per SKU.
// Pages ask for the prices of the products they show through `usePrices` (ADO-2248 DEC-1:
// products load first, then that page's prices); cards read their own SKU's entry.
//
// Modelled on `useAvailabilityStore`, keeping its same-tick batching, in-flight coalescing
// and per-caller read outcomes, but not its depot key: prices belong to the ordering ENTRY
// and the trading ACCOUNT, and a depot key misses an entry or account change at the same
// depot. A key change drops every entry, every in-flight read and the open batch, and bumps a
// generation: that generation is what orders writes across keys, so an answer for an earlier
// key (including an earlier visit to the same key) is never written. Within one key, in-flight
// sharing means two reads of one SKU never overlap, so the per-SKU write stamp availability
// relies on is kept here only as a backstop.
//
// No TTL and no persistence (ADO-2248 DEC-4): the entries are what the screens show for the current
// key, not a cache. Every `loadForSkus` asks again; a SKU already priced keeps its figure on
// screen until the new answer lands. With the flag off nothing is requested at all.

import { useEffect } from 'react';
import { create } from 'zustand';
import {
  fetchPriceQuotes,
  type PriceKind,
  type PriceQuoteDto,
  type RetailMarginDto,
  type UnpricedReason,
} from '../api/price-quotes.client';
import { useSessionStore } from '../auth/useSessionStore';
import { useSessionCartTarget } from '../basket/sessionCart';
import type { MoneyValue } from '../shared/money';
import { realPricesEnabled } from './realPrices';

/** One SKU's price state. A SKU with no entry has not been asked for yet, which the price
 *  component shows the same way as `loading`. */
export type PriceEntry =
  | { readonly state: 'loading' }
  | {
      readonly state: 'priced';
      readonly unitPrice: MoneyValue;
      /** Per unit, never part of the price; `0.00` when none applies. */
      readonly deposit: MoneyValue;
      readonly rrp?: MoneyValue;
      /** What kind of price it is (Pricing migration 18, ADO-2249 AC1). */
      readonly kind: PriceKind;
      /** The band price a promotion beat, per case; left out when the quote has none, never
       *  `0` (ADO-2249 AC2). */
      readonly wasPrice?: MoneyValue;
      /** The store's RSP before the promotion, shown struck before `rrp`; left out when the
       *  quote has none (ADO-4034). */
      readonly wasRrp?: MoneyValue;
      /** `YYYY-MM-DD`, only when a promotion set the price (ADO-2249 AC4). */
      readonly promotionEndDate?: string;
      /** The store's retail margin (ADO-1961, D-3), only when the read sends it, which it does
       *  when the store sees its margin; an unavailable one keeps the read's reason, which the
       *  PDP's margin info reads (MI-3). */
      readonly margin?: RetailMarginDto;
    }
  /** Answered with no price. `reason` never changes the wording; it decides whether a list
   *  leaves the product out (ADO-3948, `listVisibility.ts`), and is for tests and logs. */
  | { readonly state: 'unpriced'; readonly reason: UnpricedReason }
  /** Nobody answered: the request failed, or the answer for this SKU was unusable. */
  | { readonly state: 'failed' };

/**
 * What one caller's read did, as in `useAvailabilityStore`:
 * - `'answered'`: every SKU this caller asked for got an answer (priced or unpriced);
 * - `'unread'`: a request was spent and at least one of its SKUs came back unanswered;
 * - `'superseded'`: the entry or account changed, so the answer belongs to a key we left.
 */
export type ReadOutcome = 'answered' | 'unread' | 'superseded';

interface BatchOutcome {
  readonly unread: ReadonlySet<string>;
  readonly superseded: ReadonlySet<string>;
}

interface PriceStoreState {
  /** The entry and account the entries belong to (`priceKey`), or `null` before any load. */
  key: string | null;
  entries: Readonly<Record<string, PriceEntry>>;
  /**
   * Load the prices of `skuCodes` under the ordering entry `entryKey` for the trading account
   * `accountCode` (`null` when there is none to name). Resolves once the read settles, to this
   * caller's `ReadOutcome`. The account is named on the wire (ADO-2248 DEC-10) and keys the
   * store, so a change of account at the same entry drops the prices and asks again.
   */
  loadForSkus: (
    entryKey: string,
    accountCode: string | null,
    skuCodes: readonly string[],
  ) => Promise<ReadOutcome>;
  /**
   * The manual Retry (ADO-2248 PS-4): ask again for one SKU only. It shows `loading` at once,
   * then its price or `failed` again. A no-op (`'superseded'`) when the store holds another
   * entry or account than the one named, and with the flag off.
   */
  retrySku: (entryKey: string, accountCode: string | null, sku: string) => Promise<ReadOutcome>;
  /** Drop everything (tests, session hygiene). */
  reset: () => void;
}

/** A quote as the screens hold it: priced with the figures they show, or unpriced with its
 *  reason. Absent figures stay absent (never `0`); `basis` is not shown anywhere and is not
 *  kept. Shared with the search typeahead's own lookup, so both hold the same entry. */
export function priceEntryFromQuote(quote: PriceQuoteDto): PriceEntry {
  if (quote.status !== 'priced') return { state: 'unpriced', reason: quote.reason };
  return {
    state: 'priced',
    unitPrice: quote.unitPrice,
    deposit: quote.deposit,
    ...(quote.rrp === undefined ? {} : { rrp: quote.rrp }),
    kind: quote.kind,
    ...(quote.wasPrice === undefined ? {} : { wasPrice: quote.wasPrice }),
    ...(quote.wasRrp === undefined ? {} : { wasRrp: quote.wasRrp }),
    ...(quote.promotionEndDate === undefined ? {} : { promotionEndDate: quote.promotionEndDate }),
    ...(quote.margin === undefined ? {} : { margin: quote.margin }),
  };
}

/** The store key for an entry and account. Shared with the multi-buy deal store (ADO-3985), which
 *  keys its entries the same way. */
export function priceKey(entryKey: string, accountCode: string | null): string {
  return `${entryKey} ${accountCode ?? ''}`;
}

/** Bumped on every key change and reset: a batch writes only for the generation it was
 *  opened in, so an answer from before a switch, or from before a switch and back, is dropped. */
let generation = 0;
let writeStampCounter = 0;
/** Per-SKU stamp of the write that produced the current answer. Cleared on a key change. A
 *  backstop only: the generation orders writes across keys, and in-flight sharing stops two
 *  reads of one SKU overlapping within a key. */
let writeStamps: Record<string, number> = {};
/** SKU to the promise of the batch currently fetching it, for the current generation. */
let inFlightReads: Record<string, Promise<BatchOutcome>> = {};

interface PendingBatch {
  readonly entryKey: string;
  readonly accountCode: string | null;
  readonly generation: number;
  readonly skus: Set<string>;
  readonly promise: Promise<BatchOutcome>;
  readonly settle: (outcome: BatchOutcome) => void;
  /** Taken when the batch opened, so any write made after the ask outranks this answer. */
  readonly startStamp: number;
}
let pendingBatch: PendingBatch | null = null;

export const usePriceStore = create<PriceStoreState>((set, get) => {
  /** Send one accumulated batch, on a microtask after the tick that opened it. Always settles
   *  its promise and always clears its in-flight entries. */
  async function flushBatch(batch: PendingBatch): Promise<void> {
    if (pendingBatch === batch) pendingBatch = null;
    const toFetch = [...batch.skus];
    let unread: ReadonlySet<string> = new Set(toFetch);
    let superseded: ReadonlySet<string> = new Set<string>();
    try {
      if (batch.generation !== generation) {
        // The key moved between the ask and the flush: spend no request for it.
        superseded = new Set(toFetch);
        unread = new Set<string>();
        return;
      }
      const { quotes, unreadSkus } = await fetchPriceQuotes(
        batch.entryKey,
        batch.accountCode,
        toFetch,
      );
      if (batch.generation !== generation) {
        // The key moved while the request was on the wire: the answer belongs to a key we left.
        superseded = new Set(toFetch);
        unread = new Set<string>();
        return;
      }
      unread = new Set(unreadSkus);
      const next: Record<string, PriceEntry> = { ...get().entries };
      const write = (sku: string, entry: PriceEntry): void => {
        // Backstop: nothing newer can have written this SKU under this generation today,
        // because in-flight sharing stops overlapping reads; the generation check above is
        // what discards an answer for an earlier key.
        if ((writeStamps[sku] ?? 0) > batch.startStamp) return;
        writeStamps[sku] = batch.startStamp;
        next[sku] = entry;
      };
      for (const quote of quotes) {
        write(quote.skuCode, priceEntryFromQuote(quote));
      }
      for (const sku of unreadSkus) write(sku, { state: 'failed' });
      set({ entries: next });
    } catch {
      // `fetchPriceQuotes` never rejects, so reaching here means something unexpected did.
      // Nothing was learned: every SKU still showing `loading` becomes `failed`.
      if (batch.generation === generation) {
        const next: Record<string, PriceEntry> = { ...get().entries };
        for (const sku of toFetch) {
          if (next[sku]?.state === 'loading') next[sku] = { state: 'failed' };
        }
        set({ entries: next });
      }
      unread = new Set(toFetch);
      superseded = new Set<string>();
    } finally {
      for (const sku of toFetch) {
        if (inFlightReads[sku] === batch.promise) delete inFlightReads[sku];
      }
      batch.settle({ unread, superseded });
    }
  }

  /** Add `skus` to the tick's open batch (opening one if needed) and return its promise. */
  function enqueueRead(
    entryKey: string,
    accountCode: string | null,
    skus: readonly string[],
  ): Promise<BatchOutcome> {
    if (pendingBatch === null || pendingBatch.generation !== generation) {
      let settle!: (outcome: BatchOutcome) => void;
      const promise = new Promise<BatchOutcome>((resolve) => {
        settle = resolve;
      });
      const batch: PendingBatch = {
        entryKey,
        accountCode,
        generation,
        skus: new Set(),
        promise,
        settle,
        startStamp: ++writeStampCounter,
      };
      pendingBatch = batch;
      queueMicrotask(() => void flushBatch(batch));
    }
    const batch = pendingBatch;
    for (const sku of skus) {
      batch.skus.add(sku);
      inFlightReads[sku] = batch.promise;
    }
    return batch.promise;
  }

  return {
    key: null,
    entries: {},

    async loadForSkus(entryKey, accountCode, skuCodes) {
      if (!realPricesEnabled()) return 'answered'; // the flag is off: never ask
      const unique = [...new Set(skuCodes)].filter((sku) => sku !== '');
      if (unique.length === 0) return 'answered';

      const key = priceKey(entryKey, accountCode);
      let entries = get().entries;
      if (get().key !== key) {
        // A new entry or account: nothing held for the old one may stay on screen, and no
        // answer still on the wire for it may land.
        generation += 1;
        writeStamps = {};
        inFlightReads = {};
        pendingBatch = null;
        entries = {};
      }
      // A SKU with nothing to show yet shows the loading state; one already answered keeps
      // its answer on screen until the new one lands.
      const loading = unique.filter((sku) => entries[sku] === undefined);
      if (loading.length > 0 || get().key !== key) {
        const next: Record<string, PriceEntry> = { ...entries };
        for (const sku of loading) next[sku] = { state: 'loading' };
        set({ key, entries: next });
      }

      const riding = new Set<Promise<BatchOutcome>>();
      const toFetch: string[] = [];
      for (const sku of unique) {
        const existing = inFlightReads[sku];
        if (existing !== undefined) riding.add(existing);
        else toFetch.push(sku);
      }
      if (toFetch.length > 0) riding.add(enqueueRead(entryKey, accountCode, toFetch));

      const outcomes = await Promise.all([...riding]);
      if (unique.some((sku) => outcomes.some((outcome) => outcome.unread.has(sku)))) {
        return 'unread';
      }
      if (unique.some((sku) => outcomes.some((outcome) => outcome.superseded.has(sku)))) {
        return 'superseded';
      }
      return 'answered';
    },

    async retrySku(entryKey, accountCode, sku) {
      if (!realPricesEnabled()) return 'answered'; // the flag is off: never ask
      if (sku === '' || get().key !== priceKey(entryKey, accountCode)) return 'superseded';
      set({ entries: { ...get().entries, [sku]: { state: 'loading' } } });
      return get().loadForSkus(entryKey, accountCode, [sku]);
    },

    reset() {
      generation += 1;
      writeStamps = {};
      inFlightReads = {};
      pendingBatch = null;
      set({ key: null, entries: {} });
    },
  };
});

/**
 * Ask for the prices of the products a page or list shows, in one request (ADO-2248 AC3).
 * Call it once per page, beside `useAvailability`, with the SKUs on screen. Re-asks when the
 * SKU set, the ordering entry or the trading account changes (ADO-2248 AC4). A no-op with the
 * flag off or with no ordering entry chosen.
 */
export function usePrices(skuCodes: readonly string[]): void {
  // Keyed on the VALUE of the SKU list: pages rebuild the array on every render.
  const skuKey = [...new Set(skuCodes)].sort().join(',');
  const entryKey = useSessionStore((s) => s.activeEntryKey);
  const accountCode = usePriceAccountCode();

  useEffect(() => {
    if (!realPricesEnabled() || entryKey === null || skuKey === '') return;
    void usePriceStore.getState().loadForSkus(entryKey, accountCode, skuKey.split(','));
  }, [skuKey, entryKey, accountCode]);
}

/**
 * The store key for the session's current ordering entry and trading account, or `null` when
 * no entry is chosen. Every reader of `entries` compares it with the store's `key`: the store
 * drops an old key's entries only when a page's `usePrices` effect next loads, which is a
 * render after the switch, and never when the entry becomes `null`.
 */
export function useCurrentPriceKey(): string | null {
  const entryKey = useSessionStore((s) => s.activeEntryKey);
  const accountCode = usePriceAccountCode();
  return entryKey === null ? null : priceKey(entryKey, accountCode);
}

/**
 * The trading account every price request names (ADO-2248 DEC-10), as a SUBSCRIBED value: the
 * trading account of the selected tile (the one picked on /choose or in the depot switcher),
 * the same account its cart uses. That is `activeTradingAccount` when one is selected (for
 * staff, the store account they picked), otherwise the account the tile's own entry offers
 * (Fruit & Veg's, not Chill's, at depot 25), and `null` when there is none (staff with no store
 * account picked, or a Value Centre offering several accounts with none chosen), when no
 * request names one. Prices are not the cart's; `useSessionCartTarget` is used because it
 * resolves exactly the selected tile's account.
 */
export function usePriceAccountCode(): string | null {
  return useSessionCartTarget()?.customerAccountCode ?? null;
}

/** The price state of one SKU for the session's current entry and account, for the price
 *  component; `undefined` (no price) when the store holds another key's entries. */
export function usePriceEntry(sku: string): PriceEntry | undefined {
  const currentKey = useCurrentPriceKey();
  return usePriceStore((s) =>
    currentKey !== null && s.key === currentKey ? s.entries[sku] : undefined,
  );
}
